import { useActionBarCrossSellAnalyticsStore } from '~/stores/actionBarCrossSellAnalyticsStore/actionBarCrossSellAnalyticsStore';
import {
  buildActionBarCrossSellCardPayloads,
  createActionBarSessionId,
  resolveActionBarHiddenReason,
  resolveActionBarMainProductType,
  slugifyActionBarCategory
} from '~/utils/actionBarCrossSellAnalytics/helpers';
import {
  ACTION_BAR_CROSS_SELL_ANALYTICS_CATEGORY,
  type ActionBarCrossSellCardTrackingPayload,
  type ActionBarCrossSellHiddenReason,
  type ActionBarMainProductType
} from '~/utils/actionBarCrossSellAnalytics/types';
import type { ActionBarCrossSellCardModel } from '~/utils/shop/buildActionBarCrossSellCards';

export function useActionBarCrossSellAnalytics() {
  const { $analytics } = useNuxtApp();
  const analyticsStore = useActionBarCrossSellAnalyticsStore();

  function trackEvent(
    params: {
      action: string;
    } & Record<string, string | number | boolean | undefined | null | object>
  ): void {
    const { action, ...rest } = params;
    $analytics.pushToDataLayer({
      event: 'click_Internal',
      category: ACTION_BAR_CROSS_SELL_ANALYTICS_CATEGORY,
      action,
      ...rest
    });
  }

  function getCoreParams(): {
    ab_session_id: string;
    cart_id?: string;
    main_product_sku: string;
    main_product_type: ActionBarMainProductType;
  } | null {
    if (
      analyticsStore.sessionId == null ||
      analyticsStore.mainProductSku == null ||
      analyticsStore.mainProductType == null
    ) {
      return null;
    }

    return {
      ab_session_id: analyticsStore.sessionId,
      main_product_sku: analyticsStore.mainProductSku,
      main_product_type: analyticsStore.mainProductType,
      ...(analyticsStore.cartId ? { cart_id: analyticsStore.cartId } : {})
    };
  }

  function startSession(params: {
    cartId?: string;
    mainProductSku: string;
    attributeSetName?: string | null;
  }): string {
    const sessionId = createActionBarSessionId();
    analyticsStore.startSession({
      sessionId,
      cartId: params.cartId,
      mainProductSku: params.mainProductSku,
      mainProductType: resolveActionBarMainProductType(params.attributeSetName)
    });
    return sessionId;
  }

  function trackShown(params: {
    cards: ReadonlyArray<ActionBarCrossSellCardModel>;
    browsedGroupIds: Readonly<Record<string, true>>;
  }): void {
    const core = getCoreParams();
    if (!core) {
      return;
    }

    const cards: ActionBarCrossSellCardTrackingPayload[] =
      buildActionBarCrossSellCardPayloads(params.cards, params.browsedGroupIds);
    const visibleCount = cards.filter((card) => card.row === 1).length;
    const hiddenByPaginationCount = cards.filter(
      (card) => card.row === 2
    ).length;

    trackEvent({
      action: 'actionBarCrossSell_shown',
      ...core,
      visible_count: visibleCount,
      hidden_by_pagination_count: hiddenByPaginationCount,
      cards
    });
  }

  function trackHidden(reason: ActionBarCrossSellHiddenReason): void {
    const core = getCoreParams();
    if (!core) {
      return;
    }

    trackEvent({
      action: 'actionBarCrossSell_hidden',
      ...core,
      hidden_reason: reason
    });
  }

  function trackLengthChanged(params: {
    groupLabel: string;
    productSku: string;
    selectedLengthSku: string;
    position: number;
  }): void {
    const core = getCoreParams();
    if (!core) {
      return;
    }

    trackEvent({
      action: 'actionBarCrossSell_lengthChanged',
      ...core,
      ab_category: slugifyActionBarCategory(params.groupLabel),
      product_sku: params.productSku,
      selected_length_sku: params.selectedLengthSku,
      position: params.position
    });
  }

  function trackAddClick(params: {
    groupLabel: string;
    productSku: string;
    selectedLengthSku: string | null;
    position: number;
    price?: number;
  }): void {
    const core = getCoreParams();
    if (!core) {
      return;
    }

    trackEvent({
      action: 'actionBarCrossSell_addClick',
      ...core,
      ab_category: slugifyActionBarCategory(params.groupLabel),
      product_sku: params.productSku,
      selected_length_sku: params.selectedLengthSku,
      position: params.position,
      ...(params.price != null ? { price: params.price } : {})
    });
  }

  function trackMoreProductsClick(remainingCount: number): void {
    const core = getCoreParams();
    if (!core) {
      return;
    }

    trackEvent({
      action: 'actionBarCrossSell_moreProductsClick',
      ...core,
      remaining_count: remainingCount
    });
  }

  function registerAddAttribution(params: {
    sku: string;
    groupLabel: string;
    position: number;
  }): void {
    const core = getCoreParams();
    if (!core) {
      return;
    }

    analyticsStore.registerAttribution(params.sku, {
      ab_session_id: core.ab_session_id,
      ab_category: slugifyActionBarCategory(params.groupLabel),
      ab_result_position: params.position,
      ab_main_product_sku: core.main_product_sku
    });
  }

  function resolveHiddenReason(params: {
    hasRecommendedProducts: boolean;
    openGroupCount: number;
    cardsAfterDeliveryFilter: number;
  }): ActionBarCrossSellHiddenReason {
    return resolveActionBarHiddenReason(params);
  }

  return {
    analyticsStore,
    startSession,
    getCoreParams,
    trackShown,
    trackHidden,
    trackLengthChanged,
    trackAddClick,
    trackMoreProductsClick,
    registerAddAttribution,
    resolveHiddenReason
  };
}
